import { Box, Divider, Stack } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { ROUTES } from 'config';
import {
  CreateCustomCategoryModal,
  CreateCustomCategoryValueModal,
} from 'containers/custom-categories';
import {
  AutocompleteInput,
  Button,
  Chip,
  CopyButton,
  DataLossPrompt,
  DateInput,
  DateTimeInput,
  Footer,
  ImageCropInput,
  ImageList,
  LabeledValue,
  Panel,
  PhoneInput,
  SelectInput,
  SwitchInput,
  TextField,
  Typography,
} from 'melp-design/components';
import { SystemColors } from 'melp-design/style';
import moment from 'moment';
import { FC, useEffect, useMemo } from 'react';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import { useCompanies } from 'store/companies';
import { useDepartments } from 'store/departments';
import { useTeams } from 'store/teams';
import { useConfirm } from 'store/confirm';
import { useCustomCategories } from 'store/custom-categories';
import {
  COGNITO_CHIP_VARIANT,
  EmployeeCognito,
  EmployeeCognitoStatus,
  EmploymentType,
  employmentTypes,
  useEmployee,
  useUnlinkEmployee,
  useUpdateEmployee,
} from 'store/employees';
import { useModalContext } from 'store/modal';
import { useUploadFile } from 'store/files';
import { ContentLanguage, timeUnits } from 'types/general';
import { formatLanguage } from 'utils/format';
import { includes } from 'utils/general';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';

interface Values {
  email: string;
  phone: string;
  personalCode: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  iban: string;
  internalComment: string;
  language: Uppercase<ContentLanguage>;
  startDate: string | null;
  avatarId: string | null;
  avatarFile: Blob | null;
  companyId: string;
  departmentId: string;
  teamId: string;
  jobTitle: string;
  employeeId: string;
  externalId: string;
  capacity: EmploymentType;
  employmentDate: string | null;
  endDate: string | null;
  categories: Array<{
    employeeCategoryId: string;
    employeeCategoryValueId: string;
  }>;
  consents: {
    shareEmail: boolean;
    sharePhoneNumber: boolean;
    shareBirthdate: boolean;
  };
}

type Translate = (key: string) => string;

const cognitoStatusCopy = (
  status: EmployeeCognitoStatus,
  t: Translate,
): { label: string; explanation: string | null } => {
  switch (status) {
    case 'linked':
      return {
        label: t('employees.app.cognito.linked'),
        explanation: null,
      };
    case 'missing':
      return {
        label: t('employees.app.cognito.missing'),
        explanation: t('employees.app.cognito.missingExplanation'),
      };
    case 'unavailable':
      return {
        label: t('employees.app.cognito.unavailable'),
        explanation: t('employees.app.cognito.unavailableExplanation'),
      };
    case 'unlinked':
      return {
        label: t('employees.app.cognito.unlinked'),
        explanation: t('employees.app.cognito.unlinkedExplanation'),
      };
  }
};

const formatCognitoUserStatus = (status: string, t: Translate) => {
  const labels: Record<string, string> = {
    CONFIRMED: t('employees.app.cognito.userStatusValue.confirmed'),
    UNCONFIRMED: t('employees.app.cognito.userStatusValue.unconfirmed'),
    RESET_REQUIRED: t('employees.app.cognito.userStatusValue.resetRequired'),
    FORCE_CHANGE_PASSWORD: t(
      'employees.app.cognito.userStatusValue.forceChangePassword',
    ),
    ARCHIVED: t('employees.app.cognito.userStatusValue.archived'),
    COMPROMISED: t('employees.app.cognito.userStatusValue.compromised'),
    UNKNOWN: t('employees.app.cognito.userStatusValue.unknown'),
  };

  return labels[status] ?? status;
};

const CognitoField: FC<{
  label: string;
  value: string;
  copyable?: boolean;
}> = ({ label, value, copyable = false }) => (
  <>
    <Typography variant="p2" color="textSecondary">
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
      <Typography variant="p2" sx={{ wordBreak: 'break-all' }}>
        {value}
      </Typography>
      {copyable ? <CopyButton value={value} size="sm" /> : null}
    </Stack>
  </>
);

const AppAccountSection: FC<{
  lastAction: string;
  cognito: EmployeeCognito | null;
  showUnlink: boolean;
  isUnlinking: boolean;
  canEdit: boolean;
  onUnlink: () => void;
}> = ({ lastAction, cognito, showUnlink, isUnlinking, canEdit, onUnlink }) => {
  const { t } = useTranslation();
  const identity = cognito?.status === 'linked' ? cognito.user : null;
  const copy = cognito ? cognitoStatusCopy(cognito.status, t) : null;

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <LabeledValue label={t('employees.last_action')} value={lastAction} />
        {showUnlink ? (
          <Button
            label={t('employees.user.unlink')}
            variant="danger-outline"
            disabled={isUnlinking || !canEdit}
            onClick={onUnlink}
          />
        ) : null}
      </Stack>

      {cognito && copy ? (
        <Stack
          gap={1}
          p={1.5}
          sx={{
            border: `1px solid ${SystemColors.grey[90]}`,
            borderRadius: 1,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <Typography variant="h3">
              {t('employees.app.cognito.title')}
            </Typography>
            <Chip
              label={copy.label}
              variant={COGNITO_CHIP_VARIANT[cognito.status]}
            />
          </Stack>

          {identity ? (
            <Stack
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                columnGap: 1.5,
                rowGap: 0.5,
                alignItems: 'baseline',
              }}
            >
              <CognitoField
                label={t('employees.app.cognito.userStatus')}
                value={formatCognitoUserStatus(identity.userStatus, t)}
              />
              <CognitoField
                label={t('employees.app.cognito.username')}
                value={identity.username}
                copyable
              />
              <CognitoField
                label={t('employees.app.cognito.sub')}
                value={identity.sub}
                copyable
              />
              {identity.phone ? (
                <CognitoField
                  label={t('employees.app.cognito.phone')}
                  value={identity.phone}
                  copyable
                />
              ) : null}
              {identity.email ? (
                <CognitoField
                  label={t('common.email')}
                  value={identity.email}
                  copyable
                />
              ) : null}
            </Stack>
          ) : copy.explanation ? (
            <Typography variant="p2" color="textSecondary">
              {copy.explanation}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

const getInitialValues = (
  defaultLanguage: Uppercase<ContentLanguage>,
): Values => ({
  email: '',
  phone: '',
  personalCode: '',
  firstName: '',
  lastName: '',
  dateOfBirth: null,
  iban: '',
  internalComment: '',
  language: defaultLanguage,
  startDate: null,
  avatarId: null,
  avatarFile: null,
  companyId: '',
  departmentId: '',
  teamId: '',
  jobTitle: '',
  employeeId: '',
  externalId: '',
  capacity: 'fullTime',
  employmentDate: null,
  endDate: null,
  categories: [],
  consents: {
    shareEmail: false,
    sharePhoneNumber: false,
    shareBirthdate: false,
  },
});

export const EmployeeSettings: FC = () => {
  const { t, i18n } = useTranslation();
  const { id: employeeId } = useRouteParams(ROUTES.employees.details);
  const history = useHistory();
  const { defaultLanguage, supportedLanguages } = useCompanyLanguages();
  const {
    me,
    canEditCompanies,
    canEditCustomEmployeeCategory,
    canEditEmployees,
    hasEditPermission,
    hasViewPermission,
  } = useMe();
  const canEdit = hasEditPermission(['employees']);
  const predefinedToasts = usePredefinedToasts();
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();

  const INITIAL_VALUES = useMemo(
    () => getInitialValues(defaultLanguage),
    [defaultLanguage],
  );

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { dirtyFields },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });
  const { fields: categoryFields } = useFieldArray({
    name: 'categories',
    control,
  });

  const [phone, email, companyId, departmentId, categories, avatarId] = watch([
    'phone',
    'email',
    'companyId',
    'departmentId',
    'categories',
    'avatarId',
  ]);

  const { data: employee } = useEmployee(employeeId);
  const { data: companies, isLoading: isCompaniesLoading } = useCompanies({
    status: ['active'],
    pageSize: 99999,
  });
  const { data: departments, isLoading: isDepartmentsLoading } = useDepartments(
    { companies: [companyId], pageSize: 99999 },
  );
  const { data: teams, isLoading: isTeamsLoading } = useTeams(
    { departments: [departmentId], pageSize: 99999 },
    { enabled: !!departmentId },
  );
  const { data: customCategories } = useCustomCategories();

  const { mutate: updateEmployee, isLoading: isUpdating } = useUpdateEmployee();
  const { mutate: unlinkEmployee, isLoading: isUnlinkingUser } =
    useUnlinkEmployee();
  const { mutateAsync: uploadFile, isLoading: isUploading } = useUploadFile();

  useEffect(() => {
    if (employee && customCategories) {
      reset({
        email: employee.email,
        phone: employee.phone,
        personalCode: employee.personalCode,
        firstName: employee.firstName,
        lastName: employee.lastName,
        dateOfBirth: employee.dateOfBirth,
        iban: employee.iban,
        internalComment: employee.internalComment,
        language: employee.language,
        startDate: employee.startDate,
        avatarId: employee.avatarId,
        companyId: employee.companyId,
        departmentId: employee.departmentId,
        teamId: employee.teamId,
        jobTitle: employee.jobTitle,
        employeeId: employee.employeeId,
        externalId: employee.externalId,
        capacity: employee.capacity,
        employmentDate: employee.hireDate,
        endDate: employee.endDate,
        categories: customCategories.map((category) => ({
          employeeCategoryId: category.id,
          employeeCategoryValueId:
            employee.customCategories.find((c) => c.id === category.id)?.value
              .id ?? '',
        })),
        consents: employee.consents,
      });
    }
  }, [customCategories, employee, reset]);

  const onSubmit: SubmitHandler<Values> = async ({ avatarFile, ...values }) => {
    let avatarId: string | null = values.avatarId;

    if (avatarFile) {
      const { data } = await uploadFile({
        file: new File([avatarFile], 'avatarId', {
          type: avatarFile.type,
        }),
      });

      avatarId = data.id;
    }

    updateEmployee(
      {
        employeeId,
        data: {
          ...values,
          avatarId,
          phone: values.phone || null,
          email: values.email || null,
          personalCode: values.personalCode || null,
          departmentId: values.departmentId || null,
          teamId: values.teamId || null,
          externalId: me?.parentCompany?.settings.useExternalId
            ? values.externalId || null
            : undefined,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  const isDirty = !!Object.keys(dirtyFields).length;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Panel title={t('form.authentication_details')}>
            <Stack spacing={2}>
              <Controller
                name="email"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextField
                    label={t('form.work_email')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required={!phone}
                    type="email"
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <PhoneInput
                    label={t('form.mobile_number')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required={!email}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="personalCode"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <TextField
                      label={t('form.personal_code')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      secure
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>

          <Panel title={t('common.contact_details')}>
            <Stack spacing={2}>
              <Stack flexDirection="row" gap={2}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextField
                      label={t('form.first_name')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      sx={{ width: '100%' }}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextField
                      label={t('form.last_name')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      sx={{ width: '100%' }}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />
              </Stack>

              <Stack flexDirection="row" gap={2}>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <DateInput
                      label={t('employees.date_of_birth')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      datePickerProps={{
                        maxDate: moment(),
                      }}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="iban"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextField
                      label={t('employees.iban.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />
              </Stack>

              <Controller
                name="internalComment"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextField
                    label={t('common.internal_comment')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    rows={5}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="language"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SelectInput
                    label={t('form.preferred_language')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    options={supportedLanguages.map((language) => ({
                      value: language,
                      label: formatLanguage(i18n.language, language),
                    }))}
                    required
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="startDate"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <DateTimeInput
                    label={t('employees.activate_on')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    datePickerProps={{
                      showTimeOffset: true,
                    }}
                    required
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="avatarFile"
                control={control}
                render={({ field: { name, onChange } }) => {
                  return avatarId ? (
                    <ImageList
                      items={[
                        {
                          imageId: avatarId,
                          actions: [
                            {
                              icon: Delete,
                              variant: 'neutral-outline',
                              disabled: !canEdit,
                              onClick: () =>
                                setValue('avatarId', null, {
                                  shouldDirty: true,
                                }),
                            },
                          ],
                        },
                      ]}
                    />
                  ) : (
                    <ImageCropInput
                      name={name}
                      onChange={onChange}
                      accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                      disabled={isUploading || !canEdit}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>

          <Panel title={t('employees.app.title')}>
            <Stack spacing={2}>
              <AppAccountSection
                lastAction={
                  employee?.user
                    ? moment(employee.user.lastActionDate).format('L')
                    : '-'
                }
                cognito={employee?.cognito ?? null}
                showUnlink={!!employee?.user}
                isUnlinking={isUnlinkingUser}
                canEdit={canEdit}
                onUnlink={async () => {
                  const { confirmed } = await confirmation(
                    undefined,
                    undefined,
                    'danger',
                  );

                  if (confirmed) {
                    unlinkEmployee({ employeeId });
                  }
                }}
              />

              <Stack gap={1}>
                <Typography variant="p1" color="textSecondary">
                  {t('employees.consents.label')}
                </Typography>

                <Controller
                  name="consents.shareEmail"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SwitchInput
                      label={t('employees.consents.shareEmail.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="consents.sharePhoneNumber"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SwitchInput
                      label={t('employees.consents.sharePhoneNumber.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="consents.shareBirthdate"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SwitchInput
                      label={t('employees.consents.shareBirthdate.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Panel>

          <Panel title={t('form.inside_company')}>
            <Stack spacing={2}>
              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Controller
                  name="companyId"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SelectInput
                      label={t('employees.company')}
                      name={name}
                      value={isCompaniesLoading ? '' : value}
                      onChange={(v) => {
                        onChange(v);
                        setValue('departmentId', '');
                        setValue('teamId', '');
                      }}
                      options={
                        companies?.items.map((company) => ({
                          value: company.id,
                          label: company.name,
                        })) ?? []
                      }
                      disabled={isCompaniesLoading || !canEdit}
                      required
                      ref={ref}
                    />
                  )}
                />
                {!canEditCompanies ||
                !hasViewPermission(['companies']) ? null : (
                  <Button
                    label={t('form.manage')}
                    variant="tertiary"
                    onClick={() =>
                      window.open(
                        '/more/company-settings?tab=companySettingsCompanies',
                        '_blank',
                      )
                    }
                  />
                )}
              </Stack>

              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Controller
                  name="departmentId"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <AutocompleteInput
                      label={t('employees.department')}
                      name={name}
                      value={isDepartmentsLoading ? '' : value}
                      onChange={(v) => {
                        onChange(v);
                        setValue('teamId', '');
                      }}
                      options={
                        departments?.map((department) => ({
                          value: department.id,
                          label: department.name,
                        })) ?? []
                      }
                      disabled={isDepartmentsLoading || !canEdit}
                      ref={ref}
                    />
                  )}
                />
                {!canEditCompanies ||
                !hasViewPermission(['companies']) ? null : (
                  <Button
                    label={t('form.manage')}
                    variant="tertiary"
                    onClick={() =>
                      window.open(
                        '/more/company-settings?tab=companySettingsCompanies',
                        '_blank',
                      )
                    }
                  />
                )}
              </Stack>

              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Controller
                  name="teamId"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <AutocompleteInput
                      label={t('org.team.label')}
                      name={name}
                      value={isTeamsLoading ? '' : value}
                      onChange={onChange}
                      options={
                        teams?.map((team) => ({
                          value: team.id,
                          label: team.name,
                        })) ?? []
                      }
                      disabled={!departmentId || isTeamsLoading || !canEdit}
                      ref={ref}
                    />
                  )}
                />
                {!canEditCompanies ||
                !hasViewPermission(['companies']) ? null : (
                  <Button
                    label={t('form.manage')}
                    variant="tertiary"
                    onClick={() =>
                      window.open(
                        '/more/company-settings?tab=companySettingsCompanies',
                        '_blank',
                      )
                    }
                  />
                )}
              </Stack>

              <Controller
                name="jobTitle"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextField
                    label={t('common.copywriter')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="employeeId"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextField
                    label={t('employees.employee_id')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              {me?.parentCompany?.settings.useExternalId ? (
                <Controller
                  name="externalId"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextField
                      label={t('employees.externalId.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />
              ) : null}

              <Controller
                name="capacity"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SelectInput
                    label={t('employees.capacity')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    options={employmentTypes.map((type) => ({
                      value: type,
                      label: t(`employees.employment.${type}`),
                    }))}
                    required
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Stack flexDirection="row" gap={2}>
                <Controller
                  name="employmentDate"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <DateTimeInput
                      label={t('employees.start_date')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      datePickerProps={{
                        showTimeOffset: true,
                        defaultTime: { hour: 9, minute: 0 },
                      }}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Divider orientation="vertical" variant="middle" flexItem />

                <Controller
                  name="endDate"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <DateTimeInput
                      label={t('employees.expiration_date')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      datePickerProps={{
                        showTimeOffset: true,
                      }}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />
              </Stack>

              {employee?.employmentDuration ? (
                <LabeledValue
                  label={t('automations.parameters.employeeWorkDuration')}
                  value={Object.entries(employee.employmentDuration)
                    .filter(([unit]) => includes(timeUnits, unit))
                    .map(([unit, amount]) =>
                      [
                        amount,
                        t(`common.unit.${unit}`, { count: amount }),
                      ].join(' '),
                    )
                    .join(', ')}
                />
              ) : null}
            </Stack>
          </Panel>

          <Panel
            title={t('employees.customCategories')}
            side={
              canEditCustomEmployeeCategory && (
                <Button
                  label={t('common.edit')}
                  variant="neutral-outline"
                  onClick={() =>
                    history.push(
                      '/more/company-settings?tab=companySettingsCustomEmployeeCategory',
                    )
                  }
                />
              )
            }
          >
            <Stack spacing={2}>
              {categoryFields?.map((field, i) => {
                const category = customCategories?.find(
                  (c) => c.id === field.employeeCategoryId,
                );

                return category ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    key={field.id}
                  >
                    <Controller
                      control={control}
                      name={`categories.${i}.employeeCategoryId`}
                      render={({ field: { value } }) => (
                        <input type="hidden" value={value} />
                      )}
                    />

                    <Controller
                      name={`categories.${i}.employeeCategoryValueId`}
                      control={control}
                      render={({ field: { name, value, onChange, ref } }) => (
                        <SelectInput
                          label={category?.name}
                          name={name}
                          value={value}
                          onChange={onChange}
                          options={
                            category?.values.map((value) => ({
                              value: value.id,
                              label: value.name,
                            })) ?? []
                          }
                          disabled={!canEdit}
                          ref={ref}
                        />
                      )}
                    />

                    <Button
                      label={t('actions.add_new_value')}
                      variant="tertiary"
                      disabled={!canEdit}
                      onClick={async () => {
                        const response = await openModal({
                          component: CreateCustomCategoryValueModal,
                          props: {
                            categoryId: category.id,
                            categoryName: category.name,
                          },
                        });

                        if (response.action === 'CREATED') {
                          setTimeout(() => {
                            // Delay to wait until category values are updated
                            setValue(
                              `categories.${i}.employeeCategoryValueId`,
                              response.id,
                              { shouldDirty: true },
                            );
                          }, 1);
                        }
                      }}
                    />

                    <CopyButton
                      value={
                        category?.values.find(
                          (c) => c.id === categories[i].employeeCategoryValueId,
                        )?.name ?? ''
                      }
                    />
                  </Stack>
                ) : null;
              })}

              {!canEditCustomEmployeeCategory ||
              !canEditEmployees ||
              !hasEditPermission(['employees']) ? null : (
                <Box>
                  <Button
                    label={t('custom_employee_category.create_category')}
                    onClick={() => {
                      openModal({
                        component: CreateCustomCategoryModal,
                      });
                    }}
                    variant="tertiary"
                  />
                </Box>
              )}
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap="10px">
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              variant="primary"
              type="submit"
              disabled={
                isUpdating ||
                isUnlinkingUser ||
                isUploading ||
                !hasEditPermission(['employees'])
              }
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt
        when={!hasEditPermission(['employees']) ? false : isDirty}
      />
    </>
  );
};
